import {TCurrency} from '@edna/utils';

import {EChannelType, EOperatorName, TChannelType} from 'src/constants';
import {ELicensePeriod, EPaymentType} from 'src/containers/Licenses/definitions';
import {ESmsCategory} from 'src/containers/MessageMatchers/SMSForm/definitions';

export enum EWAConversation {
  REFERRAL = 'referral',
  SERVICE = 'service',
  UTILITY = 'utility',
  AUTHENTICATION = 'authentication',
  MARKETING = 'marketing',
  MARKETING_LITE = 'marketing_lite',
}

export type TMessageReport = {
  subjectId: number;
  channelType: TChannelType;
  inMessageStatistics: {
    totalCount: number;
  };
  outMessageStatistics: {
    outMessageWithoutMessageMatcherStatistics: {
      totalCount: number;
      sentCount: number;
      deliveredCount: number;
      undeliveredCount: number;
      readCount: number;
    };
    outMessageWithMessageMatcherStatistics: {
      totalCount: number;
      sentCount: number;
      deliveredCount: number;
      undeliveredCount: number;
      readCount: number;
    };
  };
};

export type TBilledStatistics = {
  conversationType?: EWAConversation;
  messageMatcherExists?: boolean;
  country: string;
  price: number;
  totalCount: number;
  totalAmount: number;
};

export type TLicenseStatistics = {
  id: string;
  licenseId: number;
  price: number;
  transactionAmount: number;
  currency: TCurrency;
  paymentType: EPaymentType;
  paymentCode: string;
  periodStartedAt: string;
  periodEndedAt: string;
  transactionId: number;
  createdAt: string;
  updatedAt: string;
  documentId: number;
};

export type TWhatsappStatistics = {
  whatsappLicenseStatistics: {
    licensePrice: number;
    licensePeriodStart?: string | null;
    licensePeriodEnd?: string | null;
    billedEventPrice: number;
    licensePeriodMonths: ELicensePeriod;
  };
  whatsappMauStatistics: {
    licensePrice: number;
    tariffMauCount: number;
    mauCount: number;
    tariffRemainingMauCount: number;
    amauCount: number;
    amauPrice: number;
    amauTotalAmount: number;
  };
  whatsappConversationStatistics: {
    countStatistics: {
      conversationType: EWAConversation;
      totalCount: number;
      freeCount: number;
      billedCount: number;
    }[];
    billedStatistics: TBilledStatistics[];
  };
};

export type TViberStatistics = {
  tariffPackagePrice: number;
  viberOutMessageBilledStatistics: TBilledStatistics[];
};

export type TSmsStatisticsTotal = {
  license?: number;
  paidTemplate: number;
  subscriptionFee?: number;
  outMessage: number;
  currency: TCurrency;
};

export enum ESmsOutMessageCategory {
  AUTH = 'AUTH',
  AUTHORIZATION = 'AUTHORIZATION',

  SERVICE = 'SERVICE',

  INFO = 'INFO',
  MARKETING = 'MRKT',
  ADV = 'ADV',
  TEMPLATE_ADV = 'TEMPLATE_ADV',
  ADVERTISEMENT = 'ADVERTISEMENT',
}

export type TSmsStatistics = {
  total: TSmsStatisticsTotal;
  subscriptionFee?: [
    {
      mobileOperator: EOperatorName;
      totalPrice: number;
      currency: TCurrency;
    },
  ];
  paidTemplate: {
    mobileOperator: EOperatorName;
    templateType: ESmsCategory;
    billedCount: number;
    unitPrice?: number;
    totalPrice?: number;
    currency?: TCurrency;
  }[];
  outMessage: {
    mobileOperator: EOperatorName;
    type: ESmsOutMessageCategory;
    segmentCount: number;
    segmentPrice?: number;
    totalPrice?: number;
    currency?: TCurrency;
  }[];
};

export enum ESmsStatisticsChart {
  LICENSE = 'LICENSE',
  TEMPLATES = 'TEMPLATES',
  SUBSCRIPTION_FEE = 'SUBSCRIPTION_FEE',
  OUT_MESSAGES = 'OUT_MESSAGES',
}

export type TSmsStatisticsChart = Record<ESmsStatisticsChart, number> & {
  total: number;
};

export type TFilters = {
  filter: string;
  channelTypes: EChannelType[] | null;
};

export const DEFAULT_MESSAGE_REPORT: TMessageReport = {
  subjectId: 0,
  channelType: EChannelType.SMS,
  inMessageStatistics: {
    totalCount: 0,
  },
  outMessageStatistics: {
    outMessageWithoutMessageMatcherStatistics: {
      totalCount: 0,
      sentCount: 0,
      deliveredCount: 0,
      undeliveredCount: 0,
      readCount: 0,
    },
    outMessageWithMessageMatcherStatistics: {
      totalCount: 0,
      sentCount: 0,
      deliveredCount: 0,
      undeliveredCount: 0,
      readCount: 0,
    },
  },
};

export const DEFAULT_WHATSAPP_STATISTICS: TWhatsappStatistics = {
  whatsappLicenseStatistics: {
    licensePrice: 0,
    licensePeriodMonths: ELicensePeriod.MONTH,
    billedEventPrice: 0,
    licensePeriodStart: '',
    licensePeriodEnd: '',
  },
  whatsappMauStatistics: {
    licensePrice: 0,
    tariffMauCount: 0,
    mauCount: 0,
    tariffRemainingMauCount: 0,
    amauCount: 0,
    amauPrice: 0,
    amauTotalAmount: 0.0,
  },
  whatsappConversationStatistics: {
    countStatistics: [
      {
        conversationType: EWAConversation.REFERRAL,
        totalCount: 0,
        freeCount: 0,
        billedCount: 0,
      },
      {
        conversationType: EWAConversation.UTILITY,
        totalCount: 0,
        freeCount: 0,
        billedCount: 0,
      },
      {
        conversationType: EWAConversation.AUTHENTICATION,
        totalCount: 0,
        freeCount: 0,
        billedCount: 0,
      },
      {
        conversationType: EWAConversation.SERVICE,
        totalCount: 0,
        freeCount: 0,
        billedCount: 0,
      },
      {
        conversationType: EWAConversation.MARKETING,
        totalCount: 0,
        freeCount: 0,
        billedCount: 0,
      },
    ],
    billedStatistics: [
      {
        conversationType: EWAConversation.REFERRAL,
        country: 'RUS',
        price: 0,
        totalCount: 0,
        totalAmount: 0,
      },
      {
        conversationType: EWAConversation.UTILITY,
        country: 'RUS',
        price: 0,
        totalCount: 0,
        totalAmount: 0,
      },
      {
        conversationType: EWAConversation.AUTHENTICATION,
        country: 'RUS',
        price: 0,
        totalCount: 0,
        totalAmount: 0,
      },
      {
        conversationType: EWAConversation.MARKETING,
        country: 'RUS',
        price: 0,
        totalCount: 0,
        totalAmount: 0,
      },
      {
        conversationType: EWAConversation.SERVICE,
        country: 'RUS',
        price: 0,
        totalCount: 0,
        totalAmount: 0,
      },
    ],
  },
};

export const DEFAULT_VIBER_STATISTICS: TViberStatistics = {
  tariffPackagePrice: 0,
  viberOutMessageBilledStatistics: [
    {
      messageMatcherExists: false,
      country: 'RUS',
      price: 0,
      totalCount: 0,
      totalAmount: 0,
    },
  ],
};

export type TOldSmsStatistics = {
  mobileOperator: EOperatorName;
  category: ESmsCategory;
  billedCount: number;
  averageCost?: number;
  totalCost?: number;
}[];

export type TOldSmsBilling = {
  mobileOperator: EOperatorName;
  currency: string;
  cost: number;
}[];
